import { useId, type ReactNode } from 'react';

import styles from './Section.module.css';

export interface SectionProps {
  /** The section heading, at t-heading. Also names the region for screen readers. */
  title: string;
  /** One sentence under the heading. Optional. */
  description?: string;
  /** Actions for this section only, e.g. "Add phone". They sit at the heading's right. */
  actions?: ReactNode;
  /** 2 under the page title; 3 inside another section. */
  headingLevel?: 2 | 3;
  children: ReactNode;
}

/**
 * A titled part of a page (DESIGN.md §4): the heading, its actions and a rule, then the
 * content. Stacked cards are 24px apart by their parent's gap (the content panel, screen.page,
 * a CardGrid), so every screen has the same rhythm and nothing doubles it.
 */
export function Section({ title, description, actions, headingLevel = 2, children }: SectionProps) {
  const headingId = useId();
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={styles.section} aria-labelledby={headingId} data-level={headingLevel}>
      <div className={styles.head}>
        <div className={styles.heading}>
          <Heading id={headingId} className={styles.title}>
            {title}
          </Heading>
          {description === undefined ? null : <p className={styles.description}>{description}</p>}
        </div>
        {actions === undefined ? null : <div className={styles.actions}>{actions}</div>}
      </div>
      <div className={styles.body}>{children}</div>
    </section>
  );
}
